@file:NoLiveLiterals

package com.example.borshi.web.checkout

import androidx.compose.runtime.*
import com.example.borshi.web.account.AddEditAddressDialog
import com.example.borshi.web.cart.Address
import com.example.borshi.web.cart.CartStore
import com.example.borshi.web.cart.OrderFrequency
import com.example.borshi.web.cart.OrderRequest
import com.example.borshi.web.firebase.FirebaseService
import com.example.borshi.web.firebase.SavedAddress
import kotlinx.browser.window
import kotlinx.coroutines.launch
import kotlinx.coroutines.await
import kotlin.js.Promise
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun CheckoutScreen(
    cartStore: CartStore,
    onOrderPlaced: (String) -> Unit,
    onRequireLogin: () -> Unit = {}
) {
    val scope = rememberCoroutineScope()
    if (cartStore.items.isEmpty()) {
        // Guard clause if user navigates here with empty cart
        onOrderPlaced("")
        return
    }

    // Redirect to login when user is not authenticated (addresses require auth)
    val userId = remember { FirebaseService.getCurrentUserId() }
    LaunchedEffect(userId) {
        if (userId == null) {
            onRequireLogin()
            return@LaunchedEffect
        }
    }
    if (userId == null) {
        Div({
            style {
                maxWidth(600.px)
                property("margin", "0 auto")
                padding(48.px)
                width(100.percent)
                property("text-align", "center")
            }
        }) {
            P({ style { fontSize(16.px); color(Color("#555")); marginBottom(16.px) } }) {
                Text("Please log in to continue with checkout.")
            }
            P({ style { fontSize(14.px); color(Color("#9E9E9E")) } }) {
                Text("Redirecting to login…")
            }
        }
        return
    }

    var name by remember { mutableStateOf("") }
    var phone by remember { mutableStateOf("") }
    var userEmail by remember { mutableStateOf("") }

    // ── Saved addresses ──────────────────────────────────────────
    var savedAddresses by remember { mutableStateOf<List<SavedAddress>>(emptyList()) }
    var addressesLoading by remember { mutableStateOf(true) }
    var selectedAddress by remember { mutableStateOf<SavedAddress?>(null) }
    var showAddressDialog by remember { mutableStateOf(false) }
    var editingAddress by remember { mutableStateOf<SavedAddress?>(null) }

    // ── Location ─────────────────────────────────────────────────
    var locationCaptured by remember { mutableStateOf(false) }
    var latitude by remember { mutableStateOf<Double?>(null) }
    var longitude by remember { mutableStateOf<Double?>(null) }
    var isCapturingLocation by remember { mutableStateOf(false) }
    var locationError by remember { mutableStateOf<String?>(null) }

    var paymentMethod by remember { mutableStateOf("Cash on Delivery") }
    var isPlacingOrder by remember { mutableStateOf(false) }
    var submissionError by remember { mutableStateOf<String?>(null) }

    val totals = cartStore.computeTotals()

    // Load user profile and saved addresses; pre-fill name/phone/email
    LaunchedEffect(Unit) {
        try {
            val profile = FirebaseService.getUserProfile()
            if (name.isBlank()) name = profile.name ?: ""
            if (phone.isBlank()) phone = profile.phoneNumber ?: ""
            if (userEmail.isBlank()) userEmail = profile.email ?: ""
        } catch (_: Throwable) {}

        try {
            val addrs = FirebaseService.getAddresses()
            savedAddresses = addrs
            selectedAddress = addrs.firstOrNull { it.isDefault } ?: addrs.firstOrNull()
            val addr = selectedAddress
            if (addr != null) {
                if (phone.isBlank() && addr.phone.isNotBlank()) phone = addr.phone
                if (name.isBlank() && addr.name.isNotBlank()) name = addr.name
            }
        } catch (e: Throwable) {
            console.warn("Could not load saved addresses:", e)
        } finally {
            addressesLoading = false
        }
    }

    // Add / Edit address dialog
    if (showAddressDialog) {
        AddEditAddressDialog(
            address = editingAddress,
            onDismiss = { showAddressDialog = false; editingAddress = null },
            onSave = { newAddr ->
                scope.launch {
                    try {
                        val savedId = FirebaseService.saveAddress(newAddr)
                        val updated = FirebaseService.getAddresses()
                        savedAddresses = updated
                        selectedAddress = updated.firstOrNull { it.id == savedId }
                            ?: updated.firstOrNull { it.isDefault }
                            ?: updated.firstOrNull()
                    } catch (e: Throwable) {
                        console.error("Failed to save address:", e)
                    }
                    showAddressDialog = false
                    editingAddress = null
                }
            }
        )
    }

    Div({
        style {
            maxWidth(600.px)
            property("margin", "0 auto")
            padding(16.px)
            width(100.percent)
        }
    }) {
        H1({ style { fontSize(28.px); marginBottom(24.px); textAlign("center") } }) {
            Text("Checkout")
        }

        // --- Items Summary ---
        Div({
            style {
                backgroundColor(Color.white); padding(16.px); borderRadius(12.px)
                marginBottom(16.px); property("box-shadow", "0 2px 8px rgba(0,0,0,0.1)")
            }
        }) {
            H3({ style { fontSize(16.px); margin(0.px); marginBottom(8.px) } }) { Text("Cart Items") }
            if (cartStore.oneTimeItems.isNotEmpty()) {
                P({ style { fontSize(14.px); color(Color("#555")); margin(0.px); marginBottom(4.px) } }) {
                    Text("• ${cartStore.oneTimeItems.size} One-time item(s)")
                }
            }
            if (cartStore.subscriptionItems.isNotEmpty()) {
                P({ style { fontSize(14.px); color(Color("#555")); margin(0.px) } }) {
                    Text("• ${cartStore.subscriptionItems.size} Subscription(s)")
                }
            }
        }

        Div({
            style {
                backgroundColor(Color.white); padding(24.px); borderRadius(12.px)
                property("box-shadow", "0 2px 8px rgba(0,0,0,0.1)")
                display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(16.px)
            }
        }) {
            InputGroup("Full Name", name) { name = it }
            InputGroup("Phone Number", phone) { phone = it }

            // ── Delivery Address Section ───────────────────────────────
            Div({
                style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(10.px) }
            }) {
                // Header row
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.SpaceBetween)
                        alignItems(AlignItems.Center)
                    }
                }) {
                    H3({ style { fontSize(16.px); margin(0.px); fontWeight("700"); color(Color("#0D2B2E")) } }) {
                        Text("📍 Delivery Address")
                    }
                    Button({
                        style {
                            backgroundColor(Color("#D11243")); color(Color.white)
                            border(0.px); padding(8.px, 14.px); borderRadius(8.px)
                            fontSize(13.px); fontWeight("600"); cursor("pointer")
                        }
                        onClick { editingAddress = null; showAddressDialog = true }
                    }) { Text("+ Add New Address") }
                }

                when {
                    addressesLoading -> {
                        P({ style { color(Color("#9E9E9E")); fontSize(14.px); margin(0.px) } }) {
                            Text("Loading saved addresses…")
                        }
                    }
                    savedAddresses.isEmpty() -> {
                        Div({
                            style {
                                padding(16.px); backgroundColor(Color("#FFF8F0"))
                                borderRadius(10.px); border(1.px, LineStyle.Dashed, Color("#FFB74D"))
                                textAlign("center")
                            }
                        }) {
                            P({ style { fontSize(14.px); color(Color("#E65100")); margin(0.px); marginBottom(10.px) } }) {
                                Text("No saved addresses. Please add a delivery address to continue.")
                            }
                            Button({
                                style {
                                    backgroundColor(Color("#D11243")); color(Color.white)
                                    border(0.px); padding(10.px, 20.px); borderRadius(8.px)
                                    fontSize(14.px); fontWeight("600"); cursor("pointer")
                                }
                                onClick { editingAddress = null; showAddressDialog = true }
                            }) { Text("+ Add Delivery Address") }
                        }
                    }
                    else -> {
                        savedAddresses.forEach { addr ->
                            val isSelected = selectedAddress?.id == addr.id
                            Div({
                                style {
                                    padding(14.px, 16.px); borderRadius(10.px)
                                    border(2.px, LineStyle.Solid, if (isSelected) Color("#D11243") else Color("#E0E0E0"))
                                    backgroundColor(if (isSelected) Color("#FFF5F7") else Color.white)
                                    cursor("pointer")
                                    property("transition", "border-color 0.2s, background 0.2s")
                                }
                                onClick { selectedAddress = addr }
                            }) {
                                Div({
                                    style {
                                        display(DisplayStyle.Flex)
                                        justifyContent(JustifyContent.SpaceBetween)
                                        alignItems(AlignItems.FlexStart)
                                        gap(8.px)
                                    }
                                }) {
                                    // Left: address text
                                    Div({ style { flex(1) } }) {
                                        Div({
                                            style {
                                                display(DisplayStyle.Flex); alignItems(AlignItems.Center)
                                                gap(6.px); marginBottom(4.px)
                                            }
                                        }) {
                                            Span({
                                                style {
                                                    fontSize(13.px); fontWeight("700")
                                                    color(if (isSelected) Color("#D11243") else Color("#0D2B2E"))
                                                }
                                            }) { Text(addr.name) }
                                            if (addr.isDefault) {
                                                Span({
                                                    style {
                                                        fontSize(10.px); backgroundColor(Color("#E8F5E9"))
                                                        color(Color("#2E7D32")); padding(2.px, 6.px)
                                                        borderRadius(4.px); fontWeight("700")
                                                    }
                                                }) { Text("DEFAULT") }
                                            }
                                        }
                                        val addrText = buildString {
                                            if (addr.line1.isNotBlank()) append(addr.line1)
                                            if (addr.line2.isNotBlank()) append(", ${addr.line2}")
                                            if (addr.city.isNotBlank()) append(", ${addr.city}")
                                            if (addr.state.isNotBlank()) append(", ${addr.state}")
                                            if (addr.postalCode.isNotBlank()) append(" - ${addr.postalCode}")
                                        }
                                        P({ style { fontSize(13.px); color(Color("#555")); margin(0.px); lineHeight("1.5") } }) {
                                            Text(addrText)
                                        }
                                        if (addr.phone.isNotBlank()) {
                                            P({ style { fontSize(12.px); color(Color("#9E9E9E")); margin(0.px); marginTop(2.px) } }) {
                                                Text("📞 ${addr.phone}")
                                            }
                                        }
                                    }
                                    // Right: checkmark + edit
                                    Div({
                                        style {
                                            display(DisplayStyle.Flex); flexDirection(FlexDirection.Column)
                                            alignItems(AlignItems.FlexEnd); gap(6.px)
                                        }
                                    }) {
                                        if (isSelected) {
                                            Div({
                                                style {
                                                    width(22.px); height(22.px); borderRadius(50.percent)
                                                    backgroundColor(Color("#D11243"))
                                                    display(DisplayStyle.Flex); alignItems(AlignItems.Center)
                                                    justifyContent(JustifyContent.Center)
                                                    color(Color.white); fontSize(13.px); fontWeight("bold")
                                                }
                                            }) { Text("✓") }
                                        }
                                        Span({
                                            style {
                                                fontSize(12.px); color(Color("#1565C0"))
                                                cursor("pointer"); fontWeight("600")
                                            }
                                            onClick {
                                                it.stopPropagation()
                                                editingAddress = addr
                                                showAddressDialog = true
                                            }
                                        }) { Text("✏️ Edit") }
                                    }
                                }
                            }
                        }
                    }
                }
            }
            // ── End Address Section ────────────────────────────────────

            // Location Section
            Div({
                style {
                    marginTop(8.px); padding(16.px)
                    backgroundColor(Color("#F9F9F9")); borderRadius(8.px)
                    border(1.px, LineStyle.Dashed, Color("#E0E0E0"))
                }
            }) {
                H3({ style { fontSize(16.px); marginBottom(8.px) } }) { Text("📍 Delivery Location (Optional)") }
                if (locationCaptured) {
                    P({ style { color(Color("#2E7D32")); fontSize(14.px); marginBottom(8.px) } }) {
                        Text("✅ Captured: $latitude, $longitude")
                    }
                }
                if (locationError != null) {
                    P({ style { color(Color.red); fontSize(12.px); marginBottom(8.px) } }) { Text(locationError!!) }
                }
                Button({
                    style {
                        backgroundColor(if (locationCaptured) Color("#E0E0E0") else Color("#D11243"))
                        color(if (locationCaptured) Color("#424242") else Color.white)
                        padding(8.px, 16.px); borderRadius(8.px); border(0.px)
                        fontSize(14.px); cursor("pointer")
                    }
                    onClick {
                        if (!isCapturingLocation) {
                            isCapturingLocation = true; locationError = null
                            val navigator = window.navigator.asDynamic()
                            if (navigator.geolocation != undefined) {
                                val success: (dynamic) -> Unit = { position ->
                                    latitude = position.coords.latitude as Double
                                    longitude = position.coords.longitude as Double
                                    locationCaptured = true; isCapturingLocation = false
                                }
                                val errorCb: (dynamic) -> Unit = { locationError = "Failed to get location."; isCapturingLocation = false }
                                navigator.geolocation.getCurrentPosition(success, errorCb)
                            } else { locationError = "Geolocation not supported."; isCapturingLocation = false }
                        }
                    }
                }) {
                    Text(if (isCapturingLocation) "Capturing..." else if (locationCaptured) "Update Location" else "Capture Current Location")
                }
            }

            // Payment Method Section
            Div({ style { marginTop(16.px) } }) {
                H3({ style { fontSize(18.px); marginBottom(12.px) } }) { Text("Payment Method") }
                Div({
                    style {
                        backgroundColor(Color("#E3F2FD")); padding(12.px); borderRadius(8.px)
                        marginBottom(16.px); display(DisplayStyle.Flex); alignItems(AlignItems.Start); gap(8.px)
                    }
                }) {
                    Span({ style { fontSize(20.px) } }) { Text("ℹ️") }
                    P({ style { fontSize(14.px); color(Color("#1976D2")); lineHeight("1.5"); margin(0.px) } }) {
                        Text("Make payments directly to Borshi using UPI, QR Code, or Cash on Delivery")
                    }
                }
                val methods = listOf(
                    Triple("Cashfree Payment", "Pay securely with Card, UPI, NetBanking & more", "💳"),
                    Triple("Cash on Delivery", "Pay when you receive your order", "💵")
                )
                Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(12.px) } }) {
                    methods.forEach { (mName, subtitle, icon) ->
                        EnhancedPaymentMethodRow(name = mName, subtitle = subtitle, icon = icon, selected = paymentMethod == mName) {
                            paymentMethod = mName
                        }
                    }
                }
            }

            // Cashfree info
            if (paymentMethod == "Cashfree Payment") {
                Div({
                    style {
                        marginTop(16.px); backgroundColor(Color.white); padding(20.px); borderRadius(12.px)
                        border(2.px, LineStyle.Solid, Color("#D11243")); textAlign("center")
                    }
                }) {
                    H4({ style { fontSize(18.px); fontWeight("bold"); color(Color("#D11243")); marginBottom(8.px) } }) { Text("🔒 Secure Payment Gateway") }
                    P({ style { fontSize(14.px); color(Color("#757575")); marginBottom(16.px) } }) { Text("Powered by Cashfree - India's leading payment gateway") }
                    Div({ style { display(DisplayStyle.Grid); property("grid-template-columns", "repeat(2, 1fr)"); gap(12.px); marginBottom(16.px) } }) {
                        PaymentMethodBadge("💳", "Credit/Debit Card")
                        PaymentMethodBadge("📱", "UPI Apps")
                        PaymentMethodBadge("🏦", "Net Banking")
                        PaymentMethodBadge("💰", "Wallets")
                    }
                    P({ style { fontSize(12.px); color(Color("#757575")); marginTop(12.px) } }) { Text("Click 'Place Order' to proceed to secure payment") }
                }
            }
            // Payment Summary
            Div({
                style { marginTop(16.px); padding(16.px); backgroundColor(Color("#F9F9F9")); borderRadius(8.px) }
            }) {
                H3({ style { fontSize(18.px); marginBottom(12.px) } }) { Text("Payment Details") }
                if (totals.oneTimeSubtotal > 0) SummaryRow("One-Time Items", totals.oneTimeSubtotal)
                if (totals.subscriptionFirstDeliverySubtotal > 0) SummaryRow("Subscription (1st Delivery)", totals.subscriptionFirstDeliverySubtotal)
                SummaryRow("Delivery Fee", totals.deliveryFee, if (totals.deliveryFee == 0.0) "FREE" else null)
                Div({ style { marginTop(12.px); property("border-top", "1px solid #DDD"); paddingTop(12.px) } })
                Div({
                    style {
                        display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween)
                        fontSize(18.px); fontWeight("bold"); color(Color("#212121"))
                    }
                }) {
                    Span { Text("Pay Today") }
                    Span { Text("₹${totals.payTodayTotal}") }
                }
            }

            if (submissionError != null) {
                P({ style { color(Color.red); textAlign("center"); marginTop(8.px) } }) { Text(submissionError!!) }
            }

            Button({
                style {
                    backgroundColor(if (isPlacingOrder) Color("#9E9E9E") else Color("#D11243"))
                    color(Color.white); padding(14.px); borderRadius(8.px); border(0.px)
                    fontSize(16.px); fontWeight("bold")
                    cursor(if (isPlacingOrder) "wait" else "pointer"); marginTop(16.px)
                }
                onClick {
                    if (isPlacingOrder) return@onClick

                    val addr = selectedAddress
                    if (name.isBlank() || phone.isBlank()) {
                        submissionError = "Please fill in your name and phone number."
                        return@onClick
                    }
                    if (addr == null) {
                        submissionError = "Please select or add a delivery address."
                        return@onClick
                    }

                    val invalidSubscription = cartStore.subscriptionItems.find {
                        it.schedule.frequency == OrderFrequency.CUSTOM_DAYS && it.schedule.customDays.isEmpty()
                    }
                    if (invalidSubscription != null) {
                        submissionError = "Please select delivery days for subscription: ${invalidSubscription.name}"
                        return@onClick
                    }

                    isPlacingOrder = true; submissionError = null

                    val addressLine = buildString {
                        append(addr.line1)
                        if (addr.line2.isNotBlank()) append(", ${addr.line2}")
                    }
                    val city = addr.city
                    val postalCode = addr.postalCode

                    val orderRequest = OrderRequest(
                        itemsOneTime = cartStore.oneTimeItems,
                        itemsSubscription = cartStore.subscriptionItems,
                        totals = totals,
                        customerName = name,
                        customerPhone = phone,
                        address = Address(
                            line1 = addressLine,
                            city = city,
                            postalCode = postalCode,
                            coordinates = if (latitude != null && longitude != null) latitude!! to longitude!! else null
                        ),
                        paymentMethod = paymentMethod
                    )

                    scope.launch {
                        try {
                            FirebaseService.init()
                            if (paymentMethod == "Cashfree Payment") {
                                val cartItemsArray = cartStore.oneTimeItems.map { item ->
                                    val ci = js("({})")
                                    ci.productId = item.productId; ci.productName = item.name
                                    ci.quantity = item.quantity.toInt(); ci.price = item.price; ci
                                }.toTypedArray()
                                val subItemsArray = cartStore.subscriptionItems.map { item ->
                                    val si = js("({})")
                                    si.productId = item.productId; si.productName = item.name
                                    si.price = item.price; si.packSize = item.packSize
                                    si.quantityPerDelivery = item.quantityPerDelivery
                                    si.frequency = item.schedule.frequency.name; si
                                }.toTypedArray()
                                val deliveryAddr = "${addr.line1}${if (addr.line2.isNotBlank()) ", ${addr.line2}" else ""}, ${addr.city} - ${addr.postalCode}"
                                val customerEmail = userEmail.ifBlank { "$phone@borshi.co.in" }
                                val isServiceReady = js("typeof window.cashfreeService !== 'undefined' && window.cashfreeService !== null") as Boolean
                                if (!isServiceReady) {
                                    submissionError = "Payment service failed to load. Please refresh and try again."
                                    isPlacingOrder = false; return@launch
                                }
                                val cashfreeService = kotlinx.browser.window.asDynamic().cashfreeService
                                val promise: dynamic = cashfreeService.initiateCashfreePayment(
                                    cartItemsArray, totals.payTodayTotal, deliveryAddr,
                                    name, customerEmail, phone, subItemsArray,
                                    addr.line1, addr.city, addr.postalCode, paymentMethod
                                )
                                val result: dynamic = (promise as Promise<dynamic>).await()
                                if (result.success == true) {
                                    cartStore.clear()
                                } else {
                                    submissionError = "Payment failed: ${result.message as? String ?: "Unknown error"}"
                                    isPlacingOrder = false
                                }
                            } else {
                                val orderId = FirebaseService.submitOrder(orderRequest)
                                cartStore.clear()
                                window.scrollTo(0.0, 0.0)
                                onOrderPlaced(orderId)
                            }
                        } catch (e: Throwable) {
                            console.error("Failed to place order", e)
                            submissionError = e.message ?: "Failed to place order. Please try again."
                            isPlacingOrder = false
                        }
                    }
                }
            }) {
                Text(if (isPlacingOrder) "Processing..." else "Place Order - ₹${totals.payTodayTotal}")
            }
        }
    }
}

@Composable
fun InputGroup(label: String, value: String, isTextArea: Boolean = false, onValueChange: (String) -> Unit) {
    Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(4.px) } }) {
        Label(attrs = { style { fontSize(14.px); color(Color("#666")); fontWeight("500") } }) { Text(label) }

        if (isTextArea) {
            TextArea(value) {
                onInput { onValueChange(it.value) }
                style {
                    padding(10.px)
                    borderRadius(6.px)
                    border(1.px, LineStyle.Solid, Color("#CCC"))
                    fontSize(16.px)
                    fontFamily("inherit")
                    property("resize", "vertical")
                    height(80.px)
                }
            }
        } else {
            Input(InputType.Text) {
                value(value)
                onInput { onValueChange(it.value) }
                style {
                    padding(10.px)
                    borderRadius(6.px)
                    border(1.px, LineStyle.Solid, Color("#CCC"))
                    fontSize(16.px)
                }
            }
        }
    }
}

@Composable
fun EnhancedPaymentMethodRow(
    name: String,
    subtitle: String,
    icon: String,
    selected: Boolean,
    onSelect: () -> Unit
) {
    Div({
        style {
            padding(16.px)
            border(2.px, LineStyle.Solid, if (selected) Color("#D11243") else Color("#E0E0E0"))
            borderRadius(12.px)
            backgroundColor(if (selected) Color("#FFEBEE") else Color.white)
            cursor("pointer")
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(16.px)
            property("transition", "all 0.3s ease")
            property("box-shadow", if (selected) "0 4px 12px rgba(209, 18, 67, 0.2)" else "0 2px 4px rgba(0,0,0,0.05)")
        }
        onClick { onSelect() }
        onMouseEnter { event ->
            if (!selected) {
                event.currentTarget.asDynamic().style.borderColor = "#D11243"
                event.currentTarget.asDynamic().style.transform = "translateY(-2px)"
            }
        }
        onMouseLeave { event ->
            if (!selected) {
                event.currentTarget.asDynamic().style.borderColor = "#E0E0E0"
                event.currentTarget.asDynamic().style.transform = "translateY(0)"
            }
        }
    }) {
        // Icon Box
        Div({
            style {
                width(48.px)
                height(48.px)
                borderRadius(10.px)
                backgroundColor(if (selected) Color("#D11243").unsafeCast<Color>().let {
                    Color("rgba(209, 18, 67, 0.1)")
                } else Color("#F5F5F5"))
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                fontSize(24.px)
            }
        }) {
            Text(icon)
        }

        // Text Content
        Div({
            style {
                flex(1)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(2.px)
            }
        }) {
            Span({
                style {
                    fontSize(16.px)
                    fontWeight(if (selected) "bold" else "600")
                    color(if (selected) Color("#D11243") else Color("#212121"))
                }
            }) {
                Text(name)
            }
            Span({
                style {
                    fontSize(13.px)
                    color(Color("#9E9E9E"))
                }
            }) {
                Text(subtitle)
            }
        }

        // Selection Indicator
        if (selected) {
            Div({
                style {
                    backgroundColor(Color("#D11243"))
                    color(Color.white)
                    padding(4.px, 10.px)
                    borderRadius(20.px)
                    fontSize(14.px)
                    fontWeight("bold")
                }
            }) {
                Text("✓")
            }
        }
    }
}

@Composable
fun PaymentMethodRow(name: String, selected: Boolean, onSelect: () -> Unit) {
    Div({
        style {
            padding(12.px)
            border(1.px, LineStyle.Solid, if (selected) Color("#D11243") else Color("#E0E0E0"))
            borderRadius(8.px)
            backgroundColor(if (selected) Color("#FFF0F5") else Color.white)
            cursor("pointer")
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(10.px)
        }
        onClick { onSelect() }
    }) {
        Div({
            style {
                width(16.px)
                height(16.px)
                borderRadius(50.percent)
                border(2.px, LineStyle.Solid, if (selected) Color("#D11243") else Color("#999"))
                position(Position.Relative)
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.Center)
                alignItems(AlignItems.Center)
            }
        }) {
            if (selected) {
                Div({
                    style {
                        width(8.px)
                        height(8.px)
                        borderRadius(50.percent)
                        backgroundColor(Color("#D11243"))
                    }
                })
            }
        }
        Span({ style { fontSize(16.px); fontWeight(if (selected) "bold" else "normal") } }) { Text(name) }
    }
}

@Composable
private fun SummaryRow(label: String, value: Double, trailingNote: String? = null) {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
            fontSize(14.px)
            color(Color("#444"))
            marginBottom(6.px)
        }
    }) {
        Span { Text(label) }
        Div({ style { textAlign("right") } }) {
            Span({ style { fontWeight("700") } }) { Text("₹${value}") }
            trailingNote?.let {
                Span({ style { marginLeft(6.px); fontSize(12.px); color(Color("#757575")) } }) { Text(it) }
            }
        }
    }
}

@Composable
private fun PaymentMethodBadge(icon: String, label: String) {
    Div({
        style {
            backgroundColor(Color("#F5F5F5"))
            padding(12.px)
            borderRadius(8.px)
            textAlign("center")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            gap(4.px)
        }
    }) {
        Span({ style { fontSize(24.px) } }) { Text(icon) }
        Span({ style { fontSize(12.px); color(Color("#666")); fontWeight("500") } }) { Text(label) }
    }
}
